// Basic-attack looks of the heroes who used to share one: each has its own projectile, flight trail and
// muzzle. Drawn in ink like everything else — the looks differ by shape and motion, not glow, and take
// the hero's own bolt colour (def.bolt, carried per projectile as `color`). fx.ts owns the per-projectile
// visual (visualFor), its placement and heading, and the trail throttle; this table only builds and moves.
import * as THREE from 'three';
import { mk, flat } from './engrave.js';

export type BoltKit = {
  spawn: (key: string, build: () => THREE.Object3D, dur: number, init: (it: any) => void, tick?: (it: any, k: number) => void) => any;
  puff: (pos: THREE.Vector3, color: number, opts?: { size?: number; rise?: number; dur?: number; drift?: THREE.Vector3 | null }) => void;
  fxMesh: (geo: THREE.BufferGeometry, color: number, opts?: Record<string, any>) => THREE.Mesh;
  crowMesh: () => THREE.Object3D;
  /** fx.ts's ink and crow black. */
  ink: number;
  crow: number;
};

export type BoltLook = {
  /** Used when a projectile carries no colour. */
  color: number;
  /** Seconds between trail drops. */
  every: number;
  /** Fills the projectile's group, +z forward. */
  build(g: THREE.Group, color: number): void;
  /** Per frame, after fx.ts placed and turned the group. */
  tick?(v: THREE.Object3D, t: number): void;
  /** One trail drop at the projectile's position (`pos` is scratch: copy what is kept). */
  trail(pos: THREE.Vector3, yaw: number, color: number): void;
  /** The shot leaving: `at` hand height in front of the hero, `f` the unit facing. */
  muzzle(at: THREE.Vector3, f: THREE.Vector3, big: boolean, color: number): void;
};

/** `c` with each channel scaled by `k`: a darker (k < 1) or paler (k > 1) shade of the hero's colour. */
function shade(c: number, k: number) {
  const ch = (s: number) => Math.min(255, Math.round(((c >> s) & 255) * k)) << s;
  return ch(16) | ch(8) | ch(0);
}

/* A brush stroke bent into a crescent: thick in the middle, pointed tips. Apex at the origin, bulging +y. */
function crescentGeometry(r: number, w: number, arc: number) {
  const pts: THREE.Vector2[] = [], n = 12, a0 = Math.PI / 2 - arc / 2;
  for (let i = 0; i <= n; i++) { const a = a0 + arc * i / n; pts.push(new THREE.Vector2(Math.cos(a) * r, Math.sin(a) * r)); }
  for (let i = n - 1; i >= 1; i--) {
    const u = i / n, a = a0 + arc * u, rr = r - w * Math.sin(Math.PI * u);
    pts.push(new THREE.Vector2(Math.cos(a) * rr, Math.sin(a) * rr));
  }
  return new THREE.ShapeGeometry(new THREE.Shape(pts)).translate(0, -r, 0);
}
const featherGeometry = () => new THREE.SphereGeometry(0.1, 6, 4).scale(0.4, 0.12, 1.1);
const petalGeometry = () => new THREE.SphereGeometry(0.1, 6, 4).scale(0.9, 0.2, 0.6);

export function createBolts({ spawn, puff, fxMesh, crowMesh, ink, crow }: BoltKit): Record<string, BoltLook> {
  const BACK = new THREE.Vector3(), V = new THREE.Vector3(), STILL = new THREE.Vector3();
  /** `out` = `pos` moved `d` back along the heading `yaw`. */
  const behind = (pos: THREE.Vector3, yaw: number, d: number) => BACK.set(pos.x - Math.sin(yaw) * d, pos.y, pos.z - Math.cos(yaw) * d);

  /* Something light let go at `at`: thrown by `vel`, slowed by the air, sinking with a sway — feathers, petals, flecks. */
  function drift(key: string, build: () => THREE.Object3D, at: THREE.Vector3, vel: THREE.Vector3, dur: number, fall: number, size = 1) {
    spawn(key, build, dur, (it) => {
      (it.p ||= new THREE.Vector3()).copy(at); (it.v ||= new THREE.Vector3()).copy(vel);
      it.ph = Math.random() * 6.3; it.fall = fall; it.s = size; it.d = dur;
    }, (it, k) => {
      const s = k * it.d, go = (1 - Math.exp(-3 * s)) / 3, o = it.obj;
      o.position.copy(it.p).addScaledVector(it.v, go);
      o.position.x += Math.sin(it.ph + k * 11) * 0.08 * k;
      o.position.y = Math.max(0.22, o.position.y - it.fall * s * s);
      o.rotation.set(Math.sin(it.ph + k * 9) * 0.7, it.ph + k * 2, Math.cos(it.ph + k * 8) * 0.5);
      o.scale.setScalar(it.s * (1 - k * k * k) + 0.001);
    });
  }
  /* Тень's dusk: a dark wisp drawn out along `yaw`, sinking as it thins — never a pale puff of smoke. */
  function wisp(at: THREE.Vector3, yaw: number, color: number, dur: number, size: number, stretch: number) {
    const c = shade(color, 0.6);
    spawn('b:wisp' + c, () => fxMesh(new THREE.IcosahedronGeometry(0.2, 1), c), dur, (it) => {
      (it.p ||= new THREE.Vector3()).copy(at); it.yaw = yaw; it.s = size; it.st = stretch;
    }, (it, k) => {
      it.obj.position.copy(it.p); it.obj.position.y -= k * 0.7;
      it.obj.rotation.set(0, it.yaw, 0);
      const s = (1 - k) * it.s;
      it.obj.scale.set(0.5 * s + 0.01, 0.3 * s + 0.01, (1.2 + k * 2) * it.st * s + 0.01);
    });
  }
  /* Кошмар's haze: a small dusky-violet wisp that swells a little and lifts as it fades — stippled ink, not pale smoke. */
  function haze(at: THREE.Vector3, color: number, size: number) {
    const c = shade(color, 0.7);
    spawn('b:haze' + c, () => fxMesh(new THREE.IcosahedronGeometry(0.15, 1), c), 0.5, (it) => {
      (it.p ||= new THREE.Vector3()).copy(at); it.s = size; it.ph = Math.random() * 6.3;
    }, (it, k) => {
      it.obj.position.copy(it.p); it.obj.position.y += k * 0.25;
      it.obj.rotation.set(0, it.ph, 0);
      const s = it.s * (0.7 + k * 0.6) * (1 - k * k) + 0.001;
      it.obj.scale.set(s * 1.3, s * 0.6, s);
    });
  }
  /** A random throw: `f` scaled by `fwd`, a sideways spread and a lift. */
  const toss = (f: THREE.Vector3, fwd: number, spread: number, lift: number) =>
    V.set(f.x * fwd + (Math.random() - 0.5) * spread, lift * (0.5 + Math.random() * 0.5), f.z * fwd + (Math.random() - 0.5) * spread);

  return {
    /* Грех: a crimson drop, point backwards, trailing a short sagging bead thread like Соблазн's. */
    drop: {
      color: 0xc0506a, every: 0.09,
      build(g, color) {
        const head = fxMesh(new THREE.SphereGeometry(0.15, 10, 8), color);
        const tail = fxMesh(new THREE.ConeGeometry(0.145, 0.42, 10), color);
        tail.rotation.x = -Math.PI / 2; tail.position.z = -0.2;
        g.add(head, tail);
        const beads = new THREE.Group();
        for (let i = 0; i < 6; i++) beads.add(fxMesh(new THREE.SphereGeometry(0.05, 6, 4), shade(color, 0.85)));
        g.add(beads);
        g.userData.beads = beads;
      },
      tick(v, t) {
        const beads = v.userData.beads.children;
        for (let i = 0; i < beads.length; i++) {
          const u = (i + 1) / beads.length;
          beads[i].position.set(Math.sin(t * 9 + i * 1.1) * 0.07 * u, -Math.sin(Math.PI * u * 0.8) * 0.22 + Math.sin(t * 12 + i) * 0.03, -0.4 - u * 1.1);
        }
      },
      trail(pos, yaw, color) {
        drift('b:drip' + color, () => fxMesh(new THREE.SphereGeometry(0.045, 6, 4), color), behind(pos, yaw, 1.4), V.set(0, 0, 0), 0.5, 3, 1);
      },
      muzzle(at, f, big, color) {
        for (let i = 0; i < (big ? 5 : 3); i++) drift('b:petal' + color, () => fxMesh(petalGeometry(), color, { double: true }), at, toss(f, 1.2, 1.2, 0.8), 1.1, 0.6);
      },
    },

    /* Кукольник: a wooden bobbin tumbling end over end, paying out slack cord behind it. */
    bobbin: {
      color: 0xb07a5a, every: 0.12,
      build(g, color) {
        const spool = new THREE.Group(), wood = 0xd8c094;
        spool.add(fxMesh(new THREE.CylinderGeometry(0.09, 0.09, 0.26, 8), color));
        for (const y of [-0.14, 0.14]) {
          const end = fxMesh(new THREE.CylinderGeometry(0.16, 0.16, 0.05, 10), wood);
          end.position.y = y;
          spool.add(end);
        }
        const cord = new THREE.Group();
        for (let i = 0; i < 10; i++) cord.add(fxMesh(new THREE.BoxGeometry(0.035, 0.035, 0.13), color));
        g.add(spool, cord);
        g.userData.spool = spool; g.userData.cord = cord;
      },
      tick(v, t) {
        v.userData.spool.rotation.x = t * 15;
        const cord = v.userData.cord.children;
        for (let i = 0; i < cord.length; i++) {
          const u = (i + 0.5) / cord.length, sag = Math.sin(Math.PI * u) * 0.25;
          cord[i].position.set(Math.sin(t * 7 + u * 6) * 0.1 * u, -sag, -0.15 - u * 1.2);
          cord[i].rotation.set(-Math.cos(Math.PI * u) * 0.6, Math.cos(t * 7 + u * 6) * 0.5 * u, 0);
        }
      },
      trail(pos, yaw) {
        puff(behind(pos, yaw, 0.2), 0xd8c8a0, { size: 0.3, rise: 0.15, dur: 0.4 });
      },
      muzzle(at, f, big, color) {
        // the cord flicked out of the hand: a loop that snaps open forward
        spawn('b:flick' + color, () => fxMesh(new THREE.TorusGeometry(0.22, 0.012, 3, 14, Math.PI), color), 0.08, (it) => {
          (it.p ||= new THREE.Vector3()).copy(at); it.yaw = Math.atan2(f.x, f.z);
        }, (it, k) => {
          it.obj.position.copy(it.p);
          it.obj.rotation.set(Math.PI / 2, 0, -it.yaw - Math.PI * 0.1 + k * 1.6);
          it.obj.scale.set(0.7 + k * 0.6, 0.7 + k * 0.6, 1);
        });
        for (let i = 0; i < (big ? 4 : 2); i++) puff(at, 0xd8c8a0, { size: 0.5, rise: 0.3, dur: 0.5, drift: toss(f, 0.6, 0.6, 0) });
      },
    },

    /* Кошмар: a small crow on fast wings, violet haze peeling off behind. */
    crow: {
      color: 0x8a7ab8, every: 0.05,
      build(g) {
        const bird = crowMesh();
        bird.scale.setScalar(0.9);
        g.add(bird);
        g.userData.bird = bird;
      },
      tick(v, t) {
        // shallower than the Стая's flap: seen from above, fully raised wings close into a single spike
        const bird = v.userData.bird.children;
        for (let i = 1; i < bird.length; i++) bird[i].rotation.z = bird[i].userData.side * (0.15 + Math.sin(t * 38) * 0.45);
      },
      trail(pos, yaw, color) { haze(behind(pos, yaw, 0.3), color, 0.5); },
      muzzle(at, f, big, color) {
        for (let i = 0; i < (big ? 6 : 4); i++) drift('b:crowFeather', () => mk(featherGeometry(), crow, { team: 'unit', cast: false }), at, toss(f, 1.5, 1.4, 0.6), 1.0, 0.8, 1.2);
        haze(at, color, 0.8);
      },
    },

    /* Волчица: three claw strokes swiping forward, ink flecks shaken off them. */
    claw: {
      color: 0x9aa0a8, every: 0.05,
      build(g, color) {
        const claws = new THREE.Group();
        for (let i = 0; i < 3; i++) {
          const m = fxMesh(crescentGeometry(0.42, 0.08, 1.7), shade(color, 0.8), { double: true });
          m.rotation.x = Math.PI / 2;
          m.position.set((i - 1) * 0.06, 0, -i * 0.2);
          claws.add(m);
        }
        claws.rotation.z = 0.3;
        g.add(claws);
        g.userData.claws = claws;
      },
      tick(v, t) { v.userData.claws.scale.set(1 + Math.sin(t * 20) * 0.08, 1, 1); },
      trail(pos, yaw) {
        drift('b:fleck', () => fxMesh(new THREE.TetrahedronGeometry(0.05), ink), behind(pos, yaw, 0.3), toss(STILL, 0, 0.8, 0.4), 0.45, 2.5);
      },
      muzzle(at, f, big) {
        // a short triple scratch torn into the ground in front of her, slanting across her facing
        const a = Math.atan2(f.x, f.z) + 0.7, ox = Math.sin(a), oz = Math.cos(a);
        for (let i = 0; i < 3; i++) {
          spawn('b:scratch', () => flat(fxMesh(crescentGeometry(2.4, 0.07, 0.32), ink, { double: true }), 0.23), big ? 1.2 : 0.9, (it) => {
            const o = (i - 1) * 0.2;
            it.obj.position.set(at.x + ox * o, 0.23, at.z + oz * o);
            it.obj.rotation.set(-Math.PI / 2, 0, a);
            it.s = (i === 1 ? 1 : 0.8) * (big ? 1.3 : 1);
          }, (it, k) => it.obj.scale.set(it.s * Math.min(1, k * 8) + 0.001, it.s * (1 - k) + 0.001, 1));
        }
      },
    },

    /* Тень: a flat shard with no light in it, stretched dusk wisps sinking off it. */
    sliver: {
      color: 0x6a6a8a, every: 0.045,
      build(g, color) {
        const shard = mk(new THREE.OctahedronGeometry(0.2, 0), shade(color, 0.55), { team: 'unit', cast: false });
        shard.scale.set(0.6, 0.18, 2.4);
        g.add(shard);
        g.userData.shard = shard;
      },
      tick(v, t) { v.userData.shard.rotation.z = Math.sin(t * 5) * 0.25; },
      trail(pos, yaw, color) { wisp(behind(pos, yaw, 0.35), yaw, color, 0.6, 1, 1); },
      muzzle(at, f, big, color) {
        for (let i = 0; i < (big ? 4 : 3); i++) wisp(BACK.copy(at).add(toss(f, 0.3, 0.7, 0)), Math.atan2(f.x, f.z) + (Math.random() - 0.5) * 0.6, color, 0.7, 0.9, 0.45);
      },
    },

    /* Птицелов: a fletched dart, shedding the odd feather that drifts down behind it. */
    dart: {
      color: 0xc8a878, every: 0.16,
      build(g, color) {
        const dart = new THREE.Group();
        const shaft = fxMesh(new THREE.CylinderGeometry(0.025, 0.025, 0.7, 5), shade(color, 0.6));
        shaft.rotation.x = Math.PI / 2;
        const tip = fxMesh(new THREE.ConeGeometry(0.05, 0.16, 5), ink);
        tip.rotation.x = Math.PI / 2; tip.position.z = 0.42;
        dart.add(shaft, tip);
        for (const side of [-1, 1]) {
          const geo = new THREE.BufferGeometry();
          geo.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, -0.12, side * 0.17, 0, -0.42, 0, 0, -0.36], 3));
          geo.computeVertexNormals();
          const vane = fxMesh(geo, color, { double: true });
          vane.rotation.z = side * 0.35;
          dart.add(vane);
        }
        g.add(dart);
        g.userData.dart = dart;
      },
      tick(v, t) { v.userData.dart.rotation.z = Math.sin(t * 6) * 0.4; },
      trail(pos, yaw, color) {
        if (Math.random() < 0.5) return;
        drift('b:feather' + color, () => fxMesh(featherGeometry(), color, { double: true }), behind(pos, yaw, 0.4), V.set(0, 0.3, 0), 1.0, 0.5);
      },
      muzzle(at, f, big, color) {
        for (let i = 0; i < (big ? 6 : 4); i++) drift('b:feather' + color, () => fxMesh(featherGeometry(), color, { double: true }), at, toss(f, 1.4, 1.6, 1.0), 1.0, 0.6, 1.2);
      },
    },
  };
}
